import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import {
  defaultNewProjectJourneyState,
  OrganisationTypeConstants,
  OrganisationTypeLabels,
  UserTypeLabels,
} from "../../../../constants";
import {
  CreatedProjects,
  NewProjectJourneyState,
  NewProjectJourneyStep,
  ProjectAccessStepState,
  ProjectDetailsStepState,
} from "../../../../models";
import { getOrganisationDetails } from "../../../../service/query";
import { Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { useProjectReferenceOptions } from "../../../../utils";
import {
  CreatingOrganisation,
  NewProjectJourneyCompletion,
  NewProjectJourneyContext,
  NewProjectJourneyContextType,
  NewProjectJourneyStepDefinition,
  NewProjectJourneyVariant,
} from "./NewProjectJourneyContext";
import { NEW_PROJECT_JOURNEY_DEFAULT_STEPS } from "./newProjectJourneySteps";

const CREATING_ORGANISATION_FALLBACK_NAME = "Your organisation";

// The role label ("Asset Manager") is both the api's targetRole spelling and UserTypeLabels' value;
// the GraphQL enum ("ASSET_MANAGER") is how searchOrganisations types every other organisation, so
// the creator's own entry is typed the same way.
const resolveCreatorType = (userType: OrganisationTypeConstants): { organisationType: string; role: string } => {
  const userTypeKey = userType as unknown as string;
  const role = UserTypeLabels[userTypeKey] ?? userTypeKey;
  const organisationType =
    Object.keys(OrganisationTypeLabels).find((key) => OrganisationTypeLabels[key] === role) ?? role;
  return { organisationType, role };
};

interface NewProjectJourneyProviderProps {
  children?: ReactNode;
  /** Called once the projects (and any invitations) are written. The host decides where to go next. */
  onComplete: (completion: NewProjectJourneyCompletion) => void;
  /** Defaults to the full two-step journey. */
  steps?: NewProjectJourneyStepDefinition[];
  /** Defaults to the current user's portal: Seed is rounded, the hubs are square. */
  variant?: NewProjectJourneyVariant;
}

export const NewProjectJourneyProvider = ({
  children,
  onComplete,
  steps = NEW_PROJECT_JOURNEY_DEFAULT_STEPS,
  variant,
}: NewProjectJourneyProviderProps): JSX.Element => {
  const { currentUserType, currentOrganisationUuid, currentDeveloperUuid } = useAuth();
  const options = useProjectReferenceOptions();

  // Held in memory only: the journey starts fresh on every visit, so nothing outlives the page.
  const [state, setState] = useState<NewProjectJourneyState>(() => defaultNewProjectJourneyState(steps[0].key));
  const [creatingOrganisationName, setCreatingOrganisationName] = useState(CREATING_ORGANISATION_FALLBACK_NAME);

  const stepIndex = useCallback(
    (step: NewProjectJourneyStep): number => steps.findIndex((s) => s.key === step),
    [steps]
  );

  // Fetched here rather than in the access step, so returning to that step does not refetch it and
  // flash the fallback name in the rollup while it loads.
  useEffect(() => {
    if (!currentOrganisationUuid) return undefined;

    let isActive = true;

    getOrganisationDetails({ organisationUuid: currentOrganisationUuid })
      .then((response) => {
        if (!isActive) return;
        if (response.status === Status.Success && response.data?.displayName) {
          setCreatingOrganisationName(response.data.displayName);
        }
      })
      .catch(() => {
        if (isActive) setCreatingOrganisationName(CREATING_ORGANISATION_FALLBACK_NAME);
      });

    return () => {
      isActive = false;
    };
  }, [currentOrganisationUuid]);

  const updateDetails = useCallback((updater: (prev: ProjectDetailsStepState) => ProjectDetailsStepState) => {
    setState((prev) => {
      const details = updater(prev.details);
      return details === prev.details ? prev : { ...prev, details };
    });
  }, []);

  const updateAccess = useCallback((updater: (prev: ProjectAccessStepState) => ProjectAccessStepState) => {
    setState((prev) => {
      const access = updater(prev.access);
      return access === prev.access ? prev : { ...prev, access };
    });
  }, []);

  const setCreatedProjects = useCallback((created: CreatedProjects | null) => {
    setState((prev) => (prev.createdProjects === created ? prev : { ...prev, createdProjects: created }));
  }, []);

  const markSaveAttempted = useCallback(() => {
    setState((prev) => (prev.isSaveAttempted ? prev : { ...prev, isSaveAttempted: true }));
  }, []);

  const goToStep = useCallback((step: NewProjectJourneyStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const completeStep = useCallback(
    (step: NewProjectJourneyStep) => {
      const next = steps[stepIndex(step) + 1];
      if (!next) return;

      setState((prev) => ({
        ...prev,
        currentStep: next.key,
        // Forward-only: revisiting an earlier step and advancing again must not rewind the stepper.
        inProgressStep: stepIndex(next.key) > stepIndex(prev.inProgressStep) ? next.key : prev.inProgressStep,
      }));
    },
    [stepIndex, steps]
  );

  const clearJourney = useCallback(() => {
    setState(defaultNewProjectJourneyState(steps[0].key));
  }, [steps]);

  const creatingOrganisation = useMemo<CreatingOrganisation>(
    () => ({
      uuid: currentOrganisationUuid ?? "",
      name: creatingOrganisationName,
      isDeveloper: !!currentDeveloperUuid,
      ...resolveCreatorType(currentUserType),
    }),
    [currentOrganisationUuid, creatingOrganisationName, currentDeveloperUuid, currentUserType]
  );

  const resolvedVariant: NewProjectJourneyVariant =
    variant ?? (currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "seed" : "default");

  const value = useMemo<NewProjectJourneyContextType>(
    () => ({
      steps,
      currentStep: state.currentStep,
      inProgressStep: state.inProgressStep,
      goToStep,
      completeStep,
      state,
      updateDetails,
      updateAccess,
      setCreatedProjects,
      markSaveAttempted,
      options,
      currentUserType,
      creatingOrganisation,
      variant: resolvedVariant,
      corners: resolvedVariant === "seed" ? "rounded" : "default",
      clearJourney,
      onComplete,
    }),
    [
      steps,
      state,
      goToStep,
      completeStep,
      updateDetails,
      updateAccess,
      setCreatedProjects,
      markSaveAttempted,
      options,
      currentUserType,
      creatingOrganisation,
      resolvedVariant,
      clearJourney,
      onComplete,
    ]
  );

  return <NewProjectJourneyContext.Provider value={value}>{children}</NewProjectJourneyContext.Provider>;
};
